import { ListTableColumn, createListTableColumns, renderBooleanIcon } from "sprint-common-web";

import { renderBuzzVersionItem } from "../../../common/components/BuzzVersionItem";
import { renderShreqVersionItem } from "../../../common/components/ShreqVersionItem";
import { ContentVersionSource } from "../../../common/types/ContentVersionSource";
import { renderContentVersionSource } from "../../../common/utils/renderContentVersionSource";
import { validateVersionsMergeability } from "../../../common/utils/validateVersionsMergeability";
import { MovieVersionDetails, MovieDetails } from "../MovieDetails";
import { AddMovieVersionToMergeListButton } from "./buttons/AddMovieVersionToMergeListButton";
import { EditVersionButton } from "./buttons/EditVersionButton";
import { ViewMovieVersionAssetButton } from "./buttons/ViewMovieVersionAssetButton";
import { MovieVersionListingItem } from "./MovieVersionListingItem";

export const createMovieVersionListingColumns = (props: {
    movieDetails: MovieDetails;
    versionsToMerge: MovieVersionListingItem[];
    addVersionToMergeList(versionItem?: MovieVersionListingItem | undefined): void;
    goToPath(path: string): void;
}): ListTableColumn<MovieVersionListingItem>[] => {
    const { movieDetails, versionsToMerge, addVersionToMergeList } = props;

    const addMovieVersionToMergeListButtonDisabled = (item?: MovieVersionListingItem): boolean => {
        if (!item) return true;

        if (item.source === ContentVersionSource.Trailer || item.source === ContentVersionSource.Shreq) return true;

        if (versionsToMerge.some((version: MovieVersionListingItem) => version.id === item.id)) return true;

        const candidates = movieDetails.versions.filter((versionDetails: MovieVersionDetails) =>
            [...versionsToMerge, item].some(
                (listingItem: MovieVersionListingItem) => listingItem.id === versionDetails.id
            )
        );

        const areMergeable = validateVersionsMergeability(candidates);

        return !areMergeable;
    };

    return createListTableColumns<MovieVersionListingItem>([
        {
            type: "basic",
            key: "distributorName",
            name: "Distributor",
            minWidth: 70,
            maxWidth: 350,
            isSortable: true
        },
        {
            type: "basic",
            key: "typeName",
            name: "Type",
            minWidth: 85,
            maxWidth: 350,
            isSortable: true
        },
        {
            type: "basic",
            key: "brandings",
            name: "Brandings",
            minWidth: 70,
            maxWidth: 280,
            isSortable: false,
            isMultiline: true
        },
        {
            type: "basic",
            key: "source",
            name: "Source",
            minWidth: 65,
            maxWidth: 90,
            isSortable: false,
            onRender: renderContentVersionSource
        },
        {
            type: "basic",
            key: "buzzVersion",
            name: "Version details",
            minWidth: 70,
            maxWidth: 280,
            isSortable: false,
            isMultiline: true,
            onRender: (item) => {
                if (item?.source === ContentVersionSource.Shreq) return renderShreqVersionItem(item?.shreqVersion);
                return renderBuzzVersionItem(item?.buzzVersion);
            }
        },
        {
            type: "basic",
            key: "merged",
            name: "Merged",
            minWidth: 50,
            maxWidth: 50,
            isSortable: false,
            onRender: renderBooleanIcon
        },
        {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 96,
            maxWidth: 96,
            buttons: [
                {
                    id: "edit",
                    render: EditVersionButton,
                    isDisabled: (item) => item.source === ContentVersionSource.Shreq
                },
                {
                    id: "viewAssets",
                    render: ViewMovieVersionAssetButton
                },
                {
                    id: "AddMovieVersionToMergeList",
                    render: AddMovieVersionToMergeListButton,
                    onItemInvoked: addVersionToMergeList,
                    isDisabled: addMovieVersionToMergeListButtonDisabled
                }
            ]
        }
    ]);
};
